// Блоки экрана «Сегодня». Каждый получает свои данные (или null) и сам рисует пустое состояние.

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Badge, Button, Card, Icon, Stat, useToast } from '@/components/ui'
import { formatDuration, formatNumber, pluralRu } from '@/lib/format'
import { GoalBar } from '@/pages/goals/parts'
import { plannedTitle } from '@/pages/plan/logic'
import type { ActiveSessionInfo } from '@/offline/activeSession'

import { todayApi, useTodayMutation, type Onboarding, type Today } from './api'
import { checklistState, readinessHint, stepsPercent, WAN_STEPS, WELLBEING_LABELS } from './logic'
import { WanMark } from './WanMark'

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted">{children}</p>
}

export function SleepBlock({ sleep }: { sleep: Today['sleep'] }) {
  return (
    <Card title="Сон прошлой ночи" actions={<Link to="/sleep" className="text-sm text-accent">Подробнее</Link>}>
      {sleep && sleep.duration_s ? (
        <Stat
          label="Длительность"
          value={formatDuration(sleep.duration_s)}
          hint={[
            sleep.score !== null ? `оценка ${sleep.score}` : null,
            sleep.deep_s ? `глубокий ${formatDuration(sleep.deep_s)}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
      ) : (
        <Empty>Нет данных о сне. Они придут из Garmin после синхронизации.</Empty>
      )}
    </Card>
  )
}

export function ReadinessBlock({ data }: { data: Today['readiness'] }) {
  return (
    <Card title="Готовность и Body Battery">
      {data ? (
        <div className="grid grid-cols-2 gap-4">
          <Stat
            label="Готовность"
            value={data.readiness === null ? '—' : formatNumber(data.readiness, { digits: 0 })}
            hint={readinessHint(data.readiness)}
          />
          <Stat
            label="Body Battery"
            value={data.body_battery_max === null ? '—' : formatNumber(data.body_battery_max, { digits: 0 })}
            hint={data.body_battery_min === null ? null : `минимум ${formatNumber(data.body_battery_min, { digits: 0 })}`}
          />
        </div>
      ) : (
        <Empty>Garmin ещё не прислал готовность на сегодня.</Empty>
      )}
    </Card>
  )
}

export function StepsBlock({ data }: { data: Today['steps'] }) {
  const percent = data ? stepsPercent(data.steps, data.goal) : null
  return (
    <Card
      title="Шаги"
      actions={
        <>
          {data && data.steps >= WAN_STEPS ? <WanMark /> : null}
          <Link to="/health/steps" className="text-sm text-accent">Подробнее</Link>
        </>
      }
    >
      {data ? (
        <>
          <Stat
            label="Сегодня"
            value={formatNumber(data.steps, { digits: 0 })}
            hint={data.goal ? `цель ${formatNumber(data.goal, { digits: 0 })}` : 'цель не задана'}
          />
          {percent !== null ? (
            <div
              role="progressbar"
              aria-label="Шаги к цели"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              className="mt-3 h-2 overflow-hidden rounded-full bg-surface-2"
            >
              <div className="h-full rounded-full bg-accent" style={{ width: `${percent}%` }} />
            </div>
          ) : null}
        </>
      ) : (
        <Empty>Шагов за сегодня пока нет.</Empty>
      )}
    </Card>
  )
}

export function WeightBlock({ data, onAdd }: { data: Today['weight']; onAdd: () => void }) {
  return (
    <Card title="Вес" actions={<Link to="/health/weight" className="text-sm text-accent">График</Link>}>
      {data ? (
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Последний" value={formatNumber(data.value, { digits: 1 })} unit="кг" />
          <Stat
            label="Среднее за 7 дней"
            value={data.trend_7d === null ? '—' : formatNumber(data.trend_7d, { digits: 1 })}
            unit={data.trend_7d === null ? undefined : 'кг'}
          />
        </div>
      ) : (
        <div className="flex flex-col items-start gap-3">
          <Empty>Веса ещё нет.</Empty>
          <Button size="sm" onClick={onAdd}>
            Записать вес
          </Button>
        </div>
      )}
    </Card>
  )
}

export function PlanBlock({
  plan,
  active,
}: {
  plan: Today['plan']
  active: ActiveSessionInfo | null
}) {
  return (
    <Card title="План на сегодня">
      {active ? (
        <Link
          to="/workout"
          className="mb-3 flex min-h-11 items-center gap-2 rounded-lg bg-accent-soft px-3 py-2 text-sm text-accent"
        >
          <Icon name="workout" size={18} className="shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            {active.draft ? (
              <>Тренировка не начата: {active.title}</>
            ) : (
              <>
                Идёт тренировка: {active.title} · {active.sets_done}{' '}
                {pluralRu(active.sets_done, ['подход', 'подхода', 'подходов'])}
              </>
            )}
          </span>
          <Icon name="chevron-right" size={18} className="shrink-0" />
        </Link>
      ) : null}
      {plan.length === 0 ? (
        <div className="flex flex-col items-start gap-3">
          <Empty>На сегодня ничего не запланировано.</Empty>
          {!active ? (
            <Link to="/workout">
              <Button size="sm">Свободная тренировка</Button>
            </Link>
          ) : null}
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {plan.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-fg">{plannedTitle(p)}</div>
                {p.warnings.length > 0 ? <div className="truncate text-xs text-warning">{p.warnings[0]}</div> : null}
              </div>
              {p.status === 'done' ? (
                <Badge tone="accent">Выполнено</Badge>
              ) : p.status === 'missed' ? (
                <Badge tone="warning">Пропущено</Badge>
              ) : p.kind === 'strength' && !active ? (
                <Link to={`/workout?planned=${p.id}`}>
                  <Button variant="primary" size="sm">
                    Открыть
                  </Button>
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

export function GoalsBlock({ goals }: { goals: Today['goals'] }) {
  return (
    <Card title="Цели" actions={<Link to="/teloscope/goals" className="text-sm text-accent">Все цели</Link>}>
      {goals.length === 0 ? (
        <Empty>Целей пока нет.</Empty>
      ) : (
        <ul className="flex flex-col gap-4">
          {goals.map((g) => (
            <li key={g.id}>
              <GoalBar goal={g} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

export function ConditionsBlock({ conditions }: { conditions: Today['conditions'] }) {
  return (
    <Card title="Травмы и болезни" actions={<Link to="/health/wellbeing" className="text-sm text-accent">Журнал</Link>}>
      {conditions.length === 0 ? (
        <Empty>Активных травм нет.</Empty>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {conditions.map((c) => (
            <li key={c.id} className="flex items-center gap-2 text-sm">
              <span className="min-w-0 flex-1 truncate text-fg">{c.title}</span>
              <Badge tone={c.severity >= 3 ? 'danger' : 'warning'}>степень {c.severity}</Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

export function WellbeingBlock({ data, onAdd }: { data: Today['wellbeing']; onAdd: () => void }) {
  return (
    <Card title="Самочувствие">
      {data ? (
        <Stat
          label="Сегодня"
          value={`${data.wellbeing}/5`}
          hint={[WELLBEING_LABELS[data.wellbeing], data.energy ? `энергия ${data.energy}/5` : null].filter(Boolean).join(' · ')}
        />
      ) : (
        <div className="flex flex-col items-start gap-3">
          <Empty>Сегодня не отмечено.</Empty>
          <Button size="sm" onClick={onAdd}>
            Отметить
          </Button>
        </div>
      )}
    </Card>
  )
}

export function SupplementsBlock({ day, supplements }: { day: string; supplements: Today['supplements'] }) {
  const toast = useToast()
  const set = useTodayMutation((v: { id: string; taken: boolean }) => todayApi.setIntake(v.id, day, v.taken))
  return (
    <Card title="Добавки на сегодня" actions={<Link to="/health/supplements" className="text-sm text-accent">Все</Link>}>
      {supplements.length === 0 ? (
        <Empty>Активных добавок нет.</Empty>
      ) : (
        <ul className="flex flex-col">
          {supplements.map((s) => (
            <li key={s.id}>
              <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-[var(--accent,currentColor)]"
                  checked={s.taken}
                  disabled={set.isPending}
                  onChange={(e) =>
                    set.mutate(
                      { id: s.id, taken: e.target.checked },
                      { onError: () => toast.show({ message: 'Не получилось отметить', tone: 'danger' }) },
                    )
                  }
                />
                <span className="min-w-0 flex-1 truncate text-fg">
                  {s.name}
                  {s.dose ? <span className="text-muted"> · {s.dose}</span> : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

export function Checklist({ onboarding }: { onboarding: Onboarding }) {
  const dismiss = useTodayMutation(todayApi.dismissOnboarding)
  const state = checklistState(onboarding)
  if (!state.visible) return null
  return (
    <Card
      title="Начни отсюда"
      subtitle={`Сделано ${state.done} из ${state.total}`}
      actions={
        <Button size="sm" variant="ghost" onClick={() => dismiss.mutate(undefined)} loading={dismiss.isPending}>
          Скрыть
        </Button>
      }
    >
      <ul className="flex flex-col">
        {onboarding.items.map((item) => (
          <li key={item.key}>
            {item.done ? (
              <div className="flex min-h-11 items-center gap-3 text-sm text-muted line-through">
                <span aria-hidden>✓</span>
                <span>{item.label}</span>
                <span className="sr-only">— сделано</span>
              </div>
            ) : (
              <Link to={item.path} className="flex min-h-11 items-center gap-3 text-sm font-medium text-accent">
                <span aria-hidden>○</span>
                <span>{item.label}</span>
              </Link>
            )}
          </li>
        ))}
      </ul>
      <Link
        to="/guide"
        className="mt-2 flex min-h-11 items-center gap-2 border-t border-border pt-3 text-sm text-muted hover:text-fg"
      >
        <Icon name="info" size={18} />
        <span className="flex-1">Как всё устроено — гайд по приложению</span>
        <Icon name="chevron-right" size={18} />
      </Link>
    </Card>
  )
}
